// ─────────────────────────────────────────────────────────────────────────────
// PassedOutReports.jsx
// Students who have passed out of the school (completed 10th) — i.e. those with
// status "Graduated". Browse by batch (academic session) and class, then open a
// passed-out student's full profile + fee history via the shared StudentDetail.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState, useEffect } from "react";
import { C, fmtClass, fmtSection, Card, CardHeader, TH, TD, Badge, Avatar, Field, inputStyle, selectStyle } from "../fees/feeUI";
import { normalizeStudents, fmtDate, StatCard, StatGrid, PageHeading, Pagination, ReportToolbar, useAdminCampus } from "./reportsShared";
import { printReport, exportCsv } from "./reportExport";
import StudentDetail from "./StudentDetail";

const PER_PAGE = 12;
const PASSED_OUT = "Graduated"; // student_status used when a student passes out after 10th

const PassedOutReports = ({ students = [] }) => {
  const [selected, setSelected] = useState(null);
  const [search,   setSearch]   = useState("");
  const [sessionF, setSessionF] = useState("all");
  const [classF,   setClassF]   = useState("all");
  const [page,     setPage]     = useState(1);

  // Only passed-out (graduated) students.
  const graduates = useMemo(
    () => normalizeStudents(students).filter(s => s.status === PASSED_OUT),
    [students]
  );

  const sessions = useMemo(() => ["all", ...[...new Set(graduates.map(s => s.academic_session).filter(Boolean))].sort((a, b) => String(b).localeCompare(String(a)))], [graduates]);
  const classes  = useMemo(() => ["all", ...[...new Set(graduates.map(s => s.class).filter(Boolean))].sort()], [graduates]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return graduates.filter(s =>
      (sessionF === "all" || (s.academic_session || "") === sessionF) &&
      (classF === "all" || s.class === classF) &&
      (!q || s.name?.toLowerCase().includes(q) || String(s.rollNo).toLowerCase().includes(q))
    );
  }, [graduates, search, sessionF, classF]);

  useEffect(() => { setPage(1); }, [search, sessionF, classF]);

  const safePage = Math.min(page, Math.max(1, Math.ceil(filtered.length / PER_PAGE)));
  const rows     = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const batches = useMemo(() => new Set(graduates.map(s => s.academic_session).filter(Boolean)).size, [graduates]);

  const campus = useAdminCampus();

  const listReport = () => ({
    title: "Passed Out Students Report",
    campus,
    scopeMeta: [
      ["Batch", sessionF === "all" ? "All Batches" : sessionF],
      ["Class", classF === "all" ? "All Classes" : fmtClass(classF)],
      ["Search", search || "—"],
      ["Students", filtered.length],
    ],
    summaryCards: [
      { label: "Total Passed Out", value: graduates.length },
      { label: "Batches", value: batches },
      { label: "In View", value: filtered.length },
    ],
    sections: [{
      heading: "Passed Out Students",
      headers: ["#", "Student", "Class", "Section", "Roll No.", "Batch", "Left On"],
      rows: filtered.map((s, i) => [i + 1, s.name, fmtClass(s.class), fmtSection(s.section), s.rollNo, s.academic_session || "—", fmtDate(s.withdrawal_date)]),
    }],
  });

  if (selected) {
    return (
      <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
        <PageHeading title="Passed Out Students" subtitle="Profile and fee history of a passed-out student" />
        <StudentDetail student={selected} campus={campus} onBack={() => setSelected(null)} backLabel="Back to passed-out students" />
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <PageHeading
        actions={<ReportToolbar onExport={() => exportCsv({ filename: "passed-out-students.csv", ...listReport() })} onPrint={() => printReport(listReport())} />}
      />

      <StatGrid>
        <StatCard label="Total Passed Out" value={graduates.length} accent={C.navy} />
        <StatCard label="Batches"          value={batches}          sub="by academic session" accent={C.amber} />
        <StatCard label="In View"          value={filtered.length}  sub="after filters" accent={C.green} />
      </StatGrid>

      <Card>
        <CardHeader title="Passed Out Students" right={<Badge color="slate">{filtered.length} students</Badge>} />

        {/* Filters */}
        <div style={{ padding: "14px 20px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          <Field label="Search"><input style={{ ...inputStyle, minWidth: "220px" }} placeholder="Name or student ID…" value={search} onChange={e => setSearch(e.target.value)} /></Field>
          <Field label="Batch (Session)">
            <select style={{ ...selectStyle, minWidth: "150px" }} value={sessionF} onChange={e => setSessionF(e.target.value)}>
              {sessions.map(s => <option key={s} value={s}>{s === "all" ? "All Batches" : s}</option>)}
            </select>
          </Field>
          <Field label="Class">
            <select style={{ ...selectStyle, minWidth: "140px" }} value={classF} onChange={e => setClassF(e.target.value)}>
              {classes.map(c => <option key={c} value={c}>{c === "all" ? "All Classes" : fmtClass(c)}</option>)}
            </select>
          </Field>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><TH>#</TH><TH>Student</TH><TH>Class</TH><TH>Section</TH><TH>Roll No.</TH><TH>Batch</TH><TH>Left On</TH><TH></TH></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: "48px", textAlign: "center", color: C.slate, fontSize: "13px" }}>
                  {graduates.length === 0 ? "No passed-out students yet." : "No passed-out students match your filters."}
                </td></tr>
              ) : rows.map((s, i) => {
                const rowBg = i % 2 === 0 ? C.white : C.bg;
                return (
                  <tr key={s.id} style={{ background: rowBg, cursor: "pointer" }}
                    onClick={() => setSelected(s)}
                    title="Click to view this student's full report"
                    onMouseEnter={e => { e.currentTarget.style.background = C.blueSoft; }}
                    onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}>
                    <TD><span style={{ color: C.slateLight, fontWeight: 600 }}>{(safePage - 1) * PER_PAGE + i + 1}</span></TD>
                    <TD>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <Avatar name={s.name} />
                        <span style={{ fontWeight: 700 }}>{s.name}</span>
                      </div>
                    </TD>
                    <TD><Badge color="blue">{fmtClass(s.class)}</Badge></TD>
                    <TD>{fmtSection(s.section)}</TD>
                    <TD mono>{s.rollNo}</TD>
                    <TD>{s.academic_session || "—"}</TD>
                    <TD>{fmtDate(s.withdrawal_date)}</TD>
                    <TD right><span style={{ color: C.slateLight, display: "inline-flex" }}><svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M13 5l7 7-7 7"/></svg></span></TD>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination page={safePage} perPage={PER_PAGE} total={filtered.length} onChange={setPage} />
      </Card>
    </div>
  );
};

export default PassedOutReports;
